import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, Calendar } from 'lucide-react';

export default function WorkPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);

  useEffect(() => { loadPlans(); loadWOs(); }, []);

  const loadPlans = async () => {
    setLoading(true);
    try { const res = await api.get('/work-orders/plans/list'); setPlans(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadWOs = async () => {
    try { const res = await api.get('/work-orders'); setWorkOrders(res.data); }
    catch (e) { console.error(e); }
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary">Group multiple work orders into planned work periods.</p>
        <button className="btn btn-accent" onClick={() => setShowCreate(true)}><Plus size={15} /> Create Work Plan</button>
      </div>
      <div className="card">
        {loading ? <div className="card-body">{[...Array(3)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div> :
         plans.length === 0 ? <div className="empty-state"><Calendar /><h4>No work plans yet</h4><p>Create a work plan for a port stay, dry-dock prep, or weekly routine.</p></div> :
         <div className="table-container">
           <table>
             <thead><tr><th>Plan Name</th><th>Type</th><th>Start Date</th><th>End Date</th><th>Work Orders</th><th>Manpower</th><th>Status</th><th>Actions</th></tr></thead>
             <tbody>
               {plans.map(p => (
                 <tr key={p.id}>
                   <td><strong>{p.plan_name}</strong></td>
                   <td><span className="badge badge-draft">{p.plan_type}</span></td>
                   <td>{p.start_date}</td>
                   <td>{p.end_date}</td>
                   <td>{p.assigned_work_orders?.length || 0} WOs</td>
                   <td>{p.manpower || '—'}</td>
                   <td><span className={`badge badge-${p.status.toLowerCase()}`}>{p.status}</span></td>
                   <td>
                     <button className="btn btn-outline btn-sm">View</button>
                   </td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
        }
      </div>
      {showCreate && <CreatePlanModal workOrders={workOrders} onClose={() => setShowCreate(false)} onCreated={loadPlans} />}
    </div>
  );
}

function CreatePlanModal({ workOrders, onClose, onCreated }) {
  const [form, setForm] = useState({ plan_name: '', plan_type: 'Port Stay', start_date: '', end_date: '', manpower: '', notes: '', assigned_work_orders: [] });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post('/work-orders/plans', form); onCreated(); onClose(); }
    catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  const toggleWO = (id) => {
    const current = form.assigned_work_orders;
    setForm({ ...form, assigned_work_orders: current.includes(id) ? current.filter(w => w !== id) : [...current, id] });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Create Work Plan</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Plan Name *</label><input className="form-input" value={form.plan_name} onChange={e => setForm({ ...form, plan_name: e.target.value })} placeholder="e.g. Port Stay — Singapore Apr 2026" required /></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Plan Type</label>
                <select className="form-select" value={form.plan_type} onChange={e => setForm({ ...form, plan_type: e.target.value })}>
                  {['Routine Weekly','Port Stay','Dry-Dock Prep','Annual Survey'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Manpower</label><input className="form-input" type="number" value={form.manpower} onChange={e => setForm({ ...form, manpower: e.target.value })} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Start Date</label><input className="form-input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">End Date</label><input className="form-input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div className="form-group"><label className="form-label">Include Work Orders</label>
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 7, padding: 8 }}>
                {workOrders.filter(w => w.status !== 'Closed').map(wo => (
                  <label key={wo.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={form.assigned_work_orders.includes(wo.id)} onChange={() => toggleWO(wo.id)} />
                    <span>{wo.wo_number} — {wo.job_title}</span>
                    <span className={`badge badge-${wo.priority.toLowerCase()}`} style={{ marginLeft: 'auto' }}>{wo.priority}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create Plan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
